import React, { useState, useEffect } from 'react';
import WeeklyCalendar from './WeeklyCalendar';
import { recipeScheduleAPI } from '../../utils/api';
import './WeeklyPlanner.css';

const WeeklyPlanner = ({ user }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [scheduledRecipes, setScheduledRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize current week on component mount
  useEffect(() => {
    const today = new Date();
    const weekStart = getWeekStart(today);
    setCurrentWeekStart(weekStart);
  }, []);

  // Load scheduled recipes when week changes
  useEffect(() => {
    if (currentWeekStart && user) {
      loadScheduledRecipes();
    }
  }, [currentWeekStart, user]);

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = d.getDate() - day; // Calculate difference to Sunday
    return new Date(d.setDate(diff));
  };

  const getWeekEnd = (weekStart) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return weekEnd;
  };

  const loadScheduledRecipes = async () => {
    if (!currentWeekStart || !user) return;

    try {
      setLoading(true);
      setError(null);
      
      const weekEnd = getWeekEnd(currentWeekStart);
      const startDateStr = currentWeekStart.toISOString().split('T')[0];
      const endDateStr = weekEnd.toISOString().split('T')[0];
      
      const data = await recipeScheduleAPI.getScheduledRecipes(startDateStr, endDateStr);
      setScheduledRecipes(data || []);
    } catch (err) {
      console.error('Error loading scheduled recipes:', err);
      setError('Failed to load scheduled recipes');
      setScheduledRecipes([]);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    if (!currentWeekStart) return;
    
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newWeekStart);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    const weekStart = getWeekStart(today);
    setCurrentWeekStart(weekStart);
  };

  const formatWeekRange = (weekStart) => {
    if (!weekStart) return '';
    
    const weekEnd = getWeekEnd(weekStart);
    const options = { month: 'short', day: 'numeric' };
    
    if (weekStart.getMonth() === weekEnd.getMonth()) {
      return `${weekStart.toLocaleDateString('en-US', { month: 'short' })} ${weekStart.getDate()} - ${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
    } else {
      return `${weekStart.toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', options)}, ${weekStart.getFullYear()}`;
    }
  };

  if (!user) {
    return (
      <div className="weekly-planner">
        <div className="planner-header">
          <h2>Weekly Recipe Planner</h2>
        </div>
        <div className="auth-required">
          <p>Please sign in to view your weekly meal plan.</p>
        </div>
      </div>
    );
  }

  if (!currentWeekStart) {
    return (
      <div className="weekly-planner">
        <div className="loading">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="weekly-planner">
      <div className="planner-header">
        <h2>Weekly Recipe Planner</h2>
        <div className="week-navigation">
          <button 
            className="nav-button" 
            onClick={() => navigateWeek(-1)}
            aria-label="Previous week"
          >
            ← Previous
          </button>
          <div className="week-range">
            <span className="week-text">{formatWeekRange(currentWeekStart)}</span>
            <button 
              className="today-button" 
              onClick={goToCurrentWeek}
            >
              Today
            </button>
          </div>
          <button 
            className="nav-button" 
            onClick={() => navigateWeek(1)}
            aria-label="Next week"
          >
            Next →
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading scheduled recipes...</div>
      ) : (
        <WeeklyCalendar
          weekStart={currentWeekStart}
          scheduledRecipes={scheduledRecipes}
          onRecipeRemoved={loadScheduledRecipes}
          user={user}
          onScheduleSuccess={loadScheduledRecipes}
        />
      )}
    </div>
  );
};

export default WeeklyPlanner;